import { Box } from '@mui/material';
import {
  DateRange,
  LocalizationProvider,
  StaticDateRangePicker,
} from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { DATE_FORMAT, dayjsTz } from 'v2/core/date/date';
import { getDateTimeRange } from '../helpers';
import { CalendarPickerProps, DatePickerMode, DateTimeRange } from '../types';
import { DateRangePickerModes } from './DateRangePickerModes';
import { getTodayEndDate } from './helpers';

export function Calendar({
  dateRange,
  timeRange,
  onChange,
  shouldHideDisplayModes = false,
}: CalendarPickerProps) {
  // Required to force->refresh the selection
  const [uniqueKey, setUniqueKey] = useState('');

  const [from, to] = dateRange.map((date) => date?.utc() ?? null);

  const [datePickerMode, setDatePickerMode] =
    useState<DatePickerMode>('absolute');

  const fromDateString = from?.toString() ?? '';
  const toDateString = to?.toString() ?? '';

  const isFixedMode = datePickerMode === 'absolute';

  useEffect(() => {
    if (!isFixedMode) {
      setUniqueKey(`${fromDateString}_${toDateString}`);
    }
  }, [fromDateString, toDateString, isFixedMode]);

  useEffect(() => {
    // Change the current timeRange object to FixedDateTimeRange object
    // if absolute is the default value set in date picker.
    if (!isFixedMode) {
      return;
    }

    onChange({
      type: 'absolute',
      startAfter: from,
      endBefore: to,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onChange]);

  const onDateRangeChange = (newNativeDateRange: DateRange<Dayjs | null>) => {
    let from = newNativeDateRange[0];
    let to = newNativeDateRange[1] ? newNativeDateRange[1].endOf('day') : null;

    // Conisder recent selection as `from` date (force `to` as `now`)
    if (!isFixedMode) {
      const now = getTodayEndDate();

      if (!!to) {
        const toDateStr = to.tz().format(DATE_FORMAT);
        if (from?.tz().format(DATE_FORMAT) === toDateStr) {
          // Meant to handle same repeated date-range selection
          setUniqueKey(String(Date.now()));
        } else if (now?.tz().format(DATE_FORMAT) !== toDateStr) {
          from = to.clone().startOf('day').tz();
        }
      }

      to = now;
    }

    const newTimeRange = getDateTimeRange([from, to], datePickerMode);
    newTimeRange && onChange(newTimeRange);
  };

  const onChangeDatePickerMode = (
    timeRange: DateTimeRange,
    newDatePickerMode: DatePickerMode,
  ) => {
    if (datePickerMode !== newDatePickerMode) {
      setDatePickerMode(newDatePickerMode);
    }
    onChange(timeRange);
  };

  return (
    <Box>
      {!shouldHideDisplayModes && (
        <DateRangePickerModes
          mode={datePickerMode}
          dateRange={dateRange}
          timeRange={timeRange}
          onChangeMode={onChangeDatePickerMode}
          onChangeTimeRange={onChange}
        />
      )}
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        dateLibInstance={dayjsTz.tz}
      >
        <StaticDateRangePicker
          key={uniqueKey}
          displayStaticWrapperAs="desktop"
          calendars={2}
          value={[from, to]}
          disableFuture
          onChange={onDateRangeChange}
        />
      </LocalizationProvider>
    </Box>
  );
}
